/**
 * AlertsPage — premium real-time alerts dashboard.
 *
 * Three tabs:
 *   1. Notification Center — fired alerts (last N days), filterable
 *   2. Configure Alerts    — your alert rules (CRUD)
 *   3. Live Stream         — newest alerts pulled from /alerts/stream
 *
 * All actions persist to backend (delete, mark-read, toggle, test fire).
 * No simulated data — the stream tab polls the real endpoint.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Bell, BellOff, Plus, Trash2, CheckCircle, AlertTriangle, AlertCircle,
  X, Radio, Filter, Loader2, Sparkles, Zap, Send,
} from 'lucide-react';
import {
  getAlertConfigs, createAlertConfig, deleteAlertConfig, updateAlertConfig,
  getAlertHistory, testAlert, getLatestAlerts,
} from '../services/api';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';

const TABS = [
  { id: 'center',    label: 'Notification Center', icon: Bell },
  { id: 'configure', label: 'Configure Alerts',    icon: Filter },
  { id: 'stream',    label: 'Live Stream',         icon: Radio },
];

const ALERT_TYPES = [
  'PRICE_ABOVE', 'PRICE_BELOW', 'PRICE_CHANGE_PERCENT',
  'RSI_OVERBOUGHT', 'RSI_OVERSOLD',
  'MACD_BULLISH_CROSSOVER', 'MACD_BEARISH_CROSSOVER',
  'BOLLINGER_SQUEEZE', 'BOLLINGER_BREAKOUT',
  'VOLUME_SPIKE', 'AI_SIGNAL_HIGH_CONFIDENCE',
  'PORTFOLIO_DRAWDOWN', 'DAILY_LOSS_LIMIT', 'CUSTOM',
];

const TYPE_FILTERS = [
  { id: 'All',       match: () => true },
  { id: 'Price',     match: (t) => t.includes('PRICE') },
  { id: 'Indicator', match: (t) => ['RSI', 'MACD', 'BOLLINGER', 'VOLUME'].some(k => t.includes(k)) },
  { id: 'AI Signal', match: (t) => t.includes('AI') },
  { id: 'Risk',      match: (t) => ['PORTFOLIO', 'DAILY'].some(k => t.includes(k)) },
];

const SEVERITY_FILTERS = [
  { id: 'All',      cls: 'bg-dark-700 text-gray-400' },
  { id: 'critical', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  { id: 'warning',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { id: 'info',     cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
];

function SeverityIcon({ severity, size = 'w-4 h-4' }) {
  if (severity === 'critical') return <AlertCircle className={`${size} text-red-400`} />;
  if (severity === 'warning')  return <AlertTriangle className={`${size} text-amber-400`} />;
  return <CheckCircle className={`${size} text-blue-400`} />;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AlertsPage() {
  const [activeTab, setActiveTab] = useState('center');
  const [alerts, setAlerts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [streamMessages, setStreamMessages] = useState([]);
  const [typeFilter, setTypeFilter] = useState('All');
  const [severityFilter, setSeverityFilter] = useState('All');
  const [streamActive, setStreamActive] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAlert, setNewAlert] = useState({
    name: '', type: 'RSI_OVERSOLD', severity: 'info', cooldownMinutes: 15,
    delivery: { inApp: true, telegram: false, discord: false },
  });
  const [creating, setCreating] = useState(false);
  const [readIds, setReadIds] = useState(() => new Set(JSON.parse(localStorage.getItem('alerts:read') || '[]')));
  const [toast, setToast] = useState(null);

  // Mount-only 5s loader visibility
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);

  // Race-safe fetch refs (separate refs per endpoint so they don't fight)
  const latestAlertsFetchRef = useRef(0);
  const latestConfigsFetchRef = useRef(0);
  const latestStreamFetchRef = useRef(0);

  function showToast(msg, ms = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  }

  function persistReadIds(set) {
    setReadIds(set);
    localStorage.setItem('alerts:read', JSON.stringify(Array.from(set)));
  }

  const fetchAlerts = useCallback(() => {
    const id = ++latestAlertsFetchRef.current;
    getAlertHistory(7).then(d => {
      if (id !== latestAlertsFetchRef.current) return;
      setAlerts(d?.alerts || []);
    }).catch(() => {});
  }, []);

  const fetchConfigs = useCallback(() => {
    const id = ++latestConfigsFetchRef.current;
    getAlertConfigs().then(d => {
      if (id !== latestConfigsFetchRef.current) return;
      setConfigs(d?.configs || []);
    }).catch(() => {});
  }, []);

  const fetchStream = useCallback(() => {
    const id = ++latestStreamFetchRef.current;
    getLatestAlerts().then(d => {
      if (id !== latestStreamFetchRef.current) return;
      setStreamMessages(d?.alerts || []);
    }).catch(() => {});
  }, []);

  // Initial fetches
  useEffect(() => { fetchConfigs(); fetchAlerts(); fetchStream(); }, [fetchConfigs, fetchAlerts, fetchStream]);

  // Notification Center — poll every 5s when active
  const alertInterval = useActivePolling(5000, activeTab === 'center');
  useEffect(() => {
    if (!alertInterval) return;
    const id = setInterval(fetchAlerts, alertInterval);
    return () => clearInterval(id);
  }, [fetchAlerts, alertInterval]);

  // Live Stream — poll every 3s when active AND on stream tab AND not paused
  const streamInterval = useActivePolling(3000, activeTab === 'stream' && streamActive);
  useEffect(() => {
    if (!streamInterval) return;
    const id = setInterval(fetchStream, streamInterval);
    return () => clearInterval(id);
  }, [fetchStream, streamInterval]);

  // ─── Derived data ────────────────────────────────────────────────────
  const filteredAlerts = useMemo(() => alerts.filter(a => {
    const typePass = TYPE_FILTERS.find(f => f.id === typeFilter)?.match(a.type || '') ?? true;
    const sevPass  = severityFilter === 'All' || a.severity === severityFilter;
    return typePass && sevPass;
  }), [alerts, typeFilter, severityFilter]);

  const isUnread = (a) => !a.read && !readIds.has(a.alertId);
  const unreadCount = useMemo(() => alerts.filter(isUnread).length, [alerts, readIds]);
  const criticalRecent = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600 * 1000;
    return alerts.filter(a => a.severity === 'critical' && new Date(a.firedAt).getTime() > cutoff).length;
  }, [alerts]);
  const activeConfigsCount = useMemo(() => configs.filter(c => c.enabled !== false).length, [configs]);

  // ─── Mutations ───────────────────────────────────────────────────────
  function markAllRead() {
    const set = new Set(readIds);
    alerts.forEach(a => set.add(a.alertId));
    persistReadIds(set);
    showToast('All alerts marked read');
  }

  function dismissAlert(id) {
    const set = new Set(readIds);
    set.add(id);
    persistReadIds(set);
    setAlerts(prev => prev.filter(a => a.alertId !== id));
  }

  async function createAlert() {
    if (!newAlert.name) return;
    setCreating(true);
    try {
      const result = await createAlertConfig(newAlert);
      if (result?.config) setConfigs(c => [...c, result.config]);
      else setConfigs(c => [...c, { ...newAlert, alertId: String(Date.now()), enabled: true, fireCount: 0 }]);
      setShowCreateForm(false);
      setNewAlert({ name: '', type: 'RSI_OVERSOLD', severity: 'info', cooldownMinutes: 15, delivery: { inApp: true, telegram: false, discord: false } });
      showToast('Alert rule created');
    } catch (err) {
      showToast(`Failed: ${err.message || 'create alert'}`, 4000);
    }
    setCreating(false);
  }

  async function toggleConfig(config) {
    const next = { ...config, enabled: !config.enabled };
    setConfigs(c => c.map(x => x.alertId === config.alertId ? next : x));
    try { await updateAlertConfig(config.alertId, { enabled: next.enabled }); }
    catch {
      setConfigs(c => c.map(x => x.alertId === config.alertId ? config : x));
      showToast('Failed to toggle', 3000);
    }
  }

  async function deleteConfig(alertId) {
    if (!confirm('Delete this alert rule? This cannot be undone.')) return;
    const prev = configs;
    setConfigs(c => c.filter(x => x.alertId !== alertId));
    try { await deleteAlertConfig(alertId); showToast('Alert rule deleted'); }
    catch { setConfigs(prev); showToast('Failed to delete', 3000); }
  }

  async function testFireConfig(config) {
    try {
      const r = await testAlert({ type: config.type, severity: config.severity, delivery: config.delivery });
      showToast(`Test fired: ${r?.preview?.message || 'OK'}`, 5000);
    } catch (err) {
      showToast(`Test failed: ${err.message}`, 4000);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Premium hero ────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50 bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/30 relative">
              <Bell className="w-7 h-7 text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-dark-900">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Market Behavior Alerts
                <Sparkles className="w-5 h-5 text-amber-400" />
              </h1>
              <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-1.5">
                Real-time alerts for price, indicators, AI signals, and risk events.
                <InfoTip side="bottom">
                  14 trigger types across Price / Indicator / AI Signal / Risk / Custom. Three severities, three delivery channels, per-rule cooldown. Test-fire before saving.
                </InfoTip>
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <KPI icon={Bell}           label="Fired (7d)"      value={alerts.length}       color="amber" />
          <KPI icon={AlertCircle}    label="Critical (24h)"  value={criticalRecent}      color="red" />
          <KPI icon={Filter}         label="Active Rules"    value={activeConfigsCount}  color="violet" sub={`of ${configs.length}`} />
          <KPI icon={Radio}          label="Unread"          value={unreadCount}         color="emerald" />
        </div>
      </div>

      {/* ─── Tabs ─────────────────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold whitespace-nowrap transition-all border ${
                isActive
                  ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                  : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
              }`}>
              <Icon className="w-4 h-4" /> {tab.label}
              {tab.id === 'center' && unreadCount > 0 && (
                <span className="text-[10px] px-1.5 py-px rounded bg-red-500 text-white font-bold">{unreadCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── NOTIFICATION CENTER ─────────────────────────────────────── */}
      {activeTab === 'center' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-500" />
            {TYPE_FILTERS.map(f => (
              <button key={f.id} onClick={() => setTypeFilter(f.id)}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors border ${
                  typeFilter === f.id
                    ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
                    : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50'
                }`}>{f.id}</button>
            ))}
            <span className="w-px h-4 bg-dark-700 mx-1" />
            {SEVERITY_FILTERS.map(s => (
              <button key={s.id} onClick={() => setSeverityFilter(s.id)}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors border ${
                  severityFilter === s.id
                    ? s.cls + (s.id === 'All' ? ' border-dark-700' : '')
                    : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50'
                }`}>{s.id}</button>
            ))}
            {unreadCount > 0 && (
              <button onClick={markAllRead}
                className="ml-auto text-[11px] font-semibold text-amber-300 hover:text-amber-200 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {showLoader && alerts.length === 0 ? (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              <span className="text-sm text-gray-400">Loading alerts…</span>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
              <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <h3 className="text-white font-bold mb-1">No alerts to display</h3>
              <p className="text-gray-500 text-sm">When your alert rules fire, they'll show up here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAlerts.map(alert => {
                const unread = isUnread(alert);
                return (
                  <div key={alert.alertId}
                    className={`bg-dark-800 rounded-2xl border p-4 transition-all ${
                      unread ? 'border-amber-500/30 shadow-lg shadow-amber-500/5' : 'border-dark-700/50'
                    }`}>
                    <div className="flex items-start gap-3">
                      <SeverityIcon severity={alert.severity} size="w-5 h-5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold text-sm ${unread ? 'text-white' : 'text-gray-300'}`}>{alert.title || alert.type}</p>
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${
                            alert.severity === 'critical' ? 'bg-red-500/15 text-red-300 border-red-500/30' :
                            alert.severity === 'warning'  ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                                                            'bg-blue-500/15 text-blue-300 border-blue-500/30'
                          }`}>{alert.severity}</span>
                          {alert.pair && <span className="text-[10px] text-gray-500">{alert.pair}</span>}
                          {unread && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">NEW</span>}
                        </div>
                        <p className="text-gray-400 text-sm mt-1">{alert.message}</p>
                        <p className="text-gray-600 text-[10px] mt-1.5">{timeAgo(alert.firedAt)}</p>
                      </div>
                      <button onClick={() => dismissAlert(alert.alertId)}
                        className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 p-1 rounded hover:bg-red-500/10"
                        title="Dismiss">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── CONFIGURE ALERTS ────────────────────────────────────────── */}
      {activeTab === 'configure' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-xs text-gray-400">
              <span className="text-white font-bold">{configs.length}</span> alert rules ·{' '}
              <span className="text-emerald-400 font-bold">{activeConfigsCount}</span> active
            </p>
            <button onClick={() => setShowCreateForm(v => !v)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5">
              <Plus className="w-4 h-4" /> {showCreateForm ? 'Close form' : 'New Alert Rule'}
            </button>
          </div>

          {showCreateForm && (
            <div className="bg-dark-800 rounded-2xl border border-amber-500/30 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
              <div className="p-5 space-y-4">
                <h3 className="text-sm font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                  <Plus className="w-4 h-4" /> New Alert Rule
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Alert Name">
                    <input value={newAlert.name} onChange={e => setNewAlert(a => ({ ...a, name: e.target.value }))}
                      placeholder="e.g. RSI Oversold Alert"
                      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
                  </Field>
                  <Field label="Alert Type">
                    <select value={newAlert.type} onChange={e => setNewAlert(a => ({ ...a, type: e.target.value }))}
                      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none">
                      {ALERT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                    </select>
                  </Field>
                  <Field label="Severity">
                    <select value={newAlert.severity} onChange={e => setNewAlert(a => ({ ...a, severity: e.target.value }))}
                      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none">
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="critical">Critical</option>
                    </select>
                  </Field>
                  <Field label="Cooldown (minutes)">
                    <input type="number" value={newAlert.cooldownMinutes}
                      onChange={e => setNewAlert(a => ({ ...a, cooldownMinutes: +e.target.value }))}
                      min={1} max={1440}
                      className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none" />
                  </Field>
                </div>
                <Field label="Delivery Channels">
                  <div className="flex gap-3 flex-wrap">
                    {['inApp', 'telegram', 'discord'].map(ch => {
                      const checked = newAlert.delivery[ch];
                      const label = ch === 'inApp' ? 'In-App' : ch[0].toUpperCase() + ch.slice(1);
                      return (
                        <button key={ch} type="button"
                          onClick={() => setNewAlert(a => ({ ...a, delivery: { ...a.delivery, [ch]: !checked } }))}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                            checked
                              ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                              : 'bg-dark-700 text-gray-400 border-dark-600 hover:border-dark-500'
                          }`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {(newAlert.delivery.telegram || newAlert.delivery.discord) && (
                    <p className="text-[10px] text-gray-500 mt-2">
                      Telegram / Discord delivery requires linking your account in Settings → Integrations.
                    </p>
                  )}
                </Field>
                <div className="flex gap-2">
                  <button onClick={createAlert} disabled={creating || !newAlert.name}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-dark-900 text-sm font-bold disabled:opacity-40 flex items-center gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Create Alert
                  </button>
                  <button onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-bold border border-dark-600">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {showLoader && configs.length === 0 ? (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              <span className="text-sm text-gray-400">Loading alert rules…</span>
            </div>
          ) : configs.length === 0 ? (
            <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
              <Filter className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <h3 className="text-white font-bold mb-1">No alert rules yet</h3>
              <p className="text-gray-500 text-sm mb-4">Set up your first rule to start receiving market alerts.</p>
              <button onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-dark-900 text-sm font-bold inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Alert Rule
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {configs.map(config => (
                <div key={config.alertId} className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <SeverityIcon severity={config.severity} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white text-sm">{config.name}</p>
                        <span className="text-[10px] text-gray-500">{config.type.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                        <span>Cooldown: {config.cooldownMinutes}m</span>
                        <span>·</span>
                        <span>Fires: <span className="text-amber-300 font-bold">{config.fireCount || 0}×</span></span>
                        {config.lastFiredAt && (
                          <>
                            <span>·</span>
                            <span>Last: {timeAgo(config.lastFiredAt)}</span>
                          </>
                        )}
                        <span className="ml-1 flex items-center gap-1">
                          {config.delivery?.inApp    && <span className="bg-dark-700 px-1.5 py-0.5 rounded text-[9px]">App</span>}
                          {config.delivery?.telegram && <span className="bg-dark-700 px-1.5 py-0.5 rounded text-[9px]">Telegram</span>}
                          {config.delivery?.discord  && <span className="bg-dark-700 px-1.5 py-0.5 rounded text-[9px]">Discord</span>}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => testFireConfig(config)}
                      className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 border border-violet-500/20 flex items-center gap-1"
                      title="Send a test fire">
                      <Zap className="w-3 h-3" /> Test
                    </button>
                    <button onClick={() => toggleConfig(config)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        config.enabled ? 'bg-amber-500' : 'bg-dark-600'
                      }`}
                      title={config.enabled ? 'Disable' : 'Enable'}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        config.enabled ? 'translate-x-4' : 'translate-x-1'
                      }`} />
                    </button>
                    <button onClick={() => deleteConfig(config.alertId)}
                      className="text-gray-600 hover:text-red-400 p-1 rounded hover:bg-red-500/10 transition-colors"
                      title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── LIVE STREAM (real, from /alerts/stream) ─────────────────── */}
      {activeTab === 'stream' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Radio className={`w-4 h-4 ${streamActive ? 'text-emerald-400 animate-pulse' : 'text-gray-500'}`} />
              <span className={`text-sm font-bold ${streamActive ? 'text-emerald-300' : 'text-gray-500'}`}>
                {streamActive ? 'Live — refreshing every 3 seconds' : 'Paused'}
              </span>
            </div>
            <button onClick={() => setStreamActive(v => !v)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-all ${
                streamActive
                  ? 'bg-red-500/15 text-red-300 hover:bg-red-500/25 border-red-500/30'
                  : 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border-emerald-500/30'
              }`}>
              {streamActive ? <><BellOff className="w-3.5 h-3.5" /> Pause</> : <><Bell className="w-3.5 h-3.5" /> Resume</>}
            </button>
          </div>

          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400" />
            <div className="p-4">
              {showLoader && streamMessages.length === 0 ? (
                <div className="flex items-center justify-center gap-3 py-12">
                  <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
                  <span className="text-sm text-gray-400">Connecting to stream…</span>
                </div>
              ) : streamMessages.length === 0 ? (
                <div className="text-center py-12">
                  <Radio className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 font-bold">Stream is quiet</p>
                  <p className="text-xs text-gray-600 mt-1">No alerts have fired recently. New alerts appear here within seconds.</p>
                </div>
              ) : (
                <div className="space-y-1 max-h-[500px] overflow-y-auto">
                  {streamMessages.map((msg, i) => (
                    <div key={msg.alertId || i}
                      className="flex items-start gap-3 text-sm border-b border-dark-700/30 last:border-b-0 py-2.5 px-1 hover:bg-dark-700/30 rounded transition-colors">
                      <SeverityIcon severity={msg.severity} />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-200 text-sm font-medium">{msg.title || msg.type}</p>
                        {msg.message && <p className="text-gray-400 text-xs mt-0.5">{msg.message}</p>}
                        <p className="text-gray-600 text-[10px] mt-1">
                          {msg.firedAt ? new Date(msg.firedAt).toLocaleTimeString() : ''}
                          {msg.pair && ` · ${msg.pair}`}
                        </p>
                      </div>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border flex-shrink-0 ${
                        msg.severity === 'critical' ? 'bg-red-500/15 text-red-300 border-red-500/30' :
                        msg.severity === 'warning'  ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                                                      'bg-blue-500/15 text-blue-300 border-blue-500/30'
                      }`}>{msg.severity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-dark-600">
          {toast}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════════

function KPI({ icon: Icon, label, value, color, sub }) {
  const colors = {
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-300',
    red:     'from-red-500/10 to-rose-500/10 border-red-500/20 text-red-300',
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-300',
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-300',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-3 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 font-semibold">{label}</label>
      {children}
    </div>
  );
}
